import { useContext } from "react";
import { useParams } from "react-router";
import { ClipLoader } from "react-spinners";
import { common_utils } from "../../../../../../../common/common_utils";
import {
	SyncDirection,
	syncDirectionToName,
} from "../../../../../../../common/sesh_types";
import { sync_utils } from "../../../../../../../common/sync_utils";
import { ErrorNote } from "../../../../forms/improved_components/error_note";
import { SelectableBox } from "../../../../forms/improved_components/selectable_box";
import { trpc } from "../../../../utils/trpc";
import { GuildSettingsContext } from "../../../guild_settings";
import SyncTypeTag from "./sync_type_tag";

export default function SyncTypeBox(props: {
	syncType: SyncDirection;
	onSelect: () => void;
}) {
	const name = syncDirectionToName(props.syncType);
	const description = syncTypeToDescription(props.syncType);

	const { server_id } = useParams<{ server_id: string }>();

	const getSyncsOrAuthURLQuery = trpc.gcal.getSyncsOrAuthURL.useQuery(
		{
			guild_id: server_id,
		},
		{ staleTime: 1000 * 60 * 60 * 24 }
	);

	// Check if user has "create" permission (needed for inbound/bidirectional syncs)
	const requiresCreatePermission =
		props.syncType === SyncDirection.Inbound ||
		props.syncType === SyncDirection.Bidirectional;

	const createPermissionQuery = trpc.members.doesUserHavePermission.useQuery(
		{
			guild_id: server_id,
			permission: "create",
		},
		{
			enabled: requiresCreatePermission,
			staleTime: 1000 * 60 * 5,
		}
	);

	const { is_premium, has_sesh_pro } = useContext(GuildSettingsContext);

	let icon: JSX.Element;
	let disabled = false;
	let missingCreatePermission = false;

	// Check create permission for inbound/bidirectional syncs
	if (requiresCreatePermission) {
		if (createPermissionQuery.isLoading) {
			disabled = true;
			icon = <ClipLoader size={16} />;
		} else if (
			createPermissionQuery.isSuccess &&
			!createPermissionQuery.data
		) {
			disabled = true;
			missingCreatePermission = true;
			icon = (
				<div className="flex items-center gap-2 text-right">
					<i className="fas fa-lock"></i>
				</div>
			);
		}
	}

	if (!disabled && getSyncsOrAuthURLQuery.isSuccess) {
		if (!getSyncsOrAuthURLQuery.data.isLoggedIn) {
			disabled = true;
			return <ErrorNote error={"Not logged in"} />;
		}

		if (
			sync_utils.isSyncLimitReached(
				getSyncsOrAuthURLQuery.data.syncs,
				props.syncType,
				is_premium,
				has_sesh_pro
			)
		) {
			disabled = true;

			if (has_sesh_pro) {
				icon = (
					<>
						<i className="fas fa-lock"></i> Limit reached (30)
					</>
				);
			} else if (is_premium) {
				icon = (
					<p className="text-right">
						<i className="fas fa-lock"></i> Premium Limit reached
						<br />
						<span className="text-right">
							Upgrade to{" "}
							<a
								href={common_utils.getGuildSettingsURL(
									server_id,
									"premium"
								)}
								className="manual-link"
							>
								Sesh Pro
							</a>{" "}
							for 30 syncs
						</span>
					</p>
				);
			} else {
				icon = (
					<p className="text-right">
						<i className="fas fa-lock"></i> Free Limit reached
						<br />
						<span className="text-right">
							Get{" "}
							<a
								href={common_utils.getGuildSettingsURL(
									server_id,
									"premium"
								)}
								className="manual-link"
							>
								Premium
							</a>
						</span>
					</p>
				);
			}
		} else if (!icon) {
			icon = <i className="fas fa-chevron-right "></i>;
		}
	} else if (!disabled) {
		disabled = true;
		icon = <ClipLoader />;
	}

	return (
		<SelectableBox
			onClick={props.onSelect}
			disabled={disabled}
			bgColor="semidark"
		>
			<div className="flex flex-col text-content-primary-darker">
				<div className="flex gap-1">
					<p className="has-text-weight-bold white-text">{name}</p>
					<SyncTypeTag syncType={props.syncType} />
				</div>
				{missingCreatePermission ? (
					<p className="text-sm">
						Requires{" "}
						<a
							href={common_utils.getGuildSettingsURL(
								server_id,
								"general"
							)}
							className="manual-link"
						>
							Create Events
						</a>{" "}
						permission
					</p>
				) : (
					description
				)}
			</div>
			<div className="ml-auto self-center text-content-primary">
				{icon}
			</div>
		</SelectableBox>
	);
}

function syncTypeToDescription(syncType: SyncDirection) {
	switch (syncType) {
		case SyncDirection.Outbound:
			return <p>Export events to Google Calendar in realtime</p>;
		case SyncDirection.Inbound:
			return (
				<div>
					<p>Import events from Google Calendar in realtime</p>
				</div>
			);
		case SyncDirection.Bidirectional:
			return (
				<div>
					<p>Sync in both directions in realtime</p>
				</div>
			);
	}
}
